import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Badge, PersonCell, Modal, StatCard, fullName, useDateFilter, DateFilter, useToast, RoleBadge, ROLE_LABELS, WORK_LABELS, BONUS_LABELS, isDirty, ScopeTabs, workLabel, bonusLabel, EmployeeFilter, uniqueUsers, useSort, SortTh, BONUS_PICKABLE, EXPENSE_ONLY_TYPES, BONUS_NEEDS_DESC, BONUS_MIN_DESC, BONUS_MAX_AMOUNT, summarizeHours, AddedOn, ReviewedBy, TimeSelect, askRejectReason, fmtDay, NormCard, TeamDiffBadge } from '../components/UI';
import api from '../utils/api';

// Klucze sortowania → wartość z wiersza
const HOURS_SORT = {
  employee: h => h.user ? `${h.user.lastName} ${h.user.firstName}` : '',
  role: h => ROLE_LABELS[h.user?.role] || '',
  date: h => String(h.date),
  workType: h => workLabel(h.workType),
  totalHours: h => Number(h.totalHours),
  totalAmount: h => Number(h.totalAmount),
  status: h => h.status,
};
const BONUS_SORT = {
  employee: b => b.user ? `${b.user.lastName} ${b.user.firstName}` : '',
  role: b => ROLE_LABELS[b.user?.role] || '',
  date: b => String(b.date),
  bonusType: b => bonusLabel(b.bonusType),
  amount: b => Number(b.amount),
  status: b => b.status,
};

// Typy pracy dostępne dla danej roli (przy edycji cudzego wpisu — rola autora, nie edytującego).
// Lustro allowedWorkTypes w backend/routes/hours.js — backend i tak odrzuci typ spoza listy.
// Lustro allowedWorkTypes w backend/routes/hours.js. T76: wg roli i przełączników „Praca na magazynie" / „na eventach".
const workTypesFor = (u) => {
  if (!u) return ['EVENT'];
  // eventowy z magazynem: na tym ekranie wpisuje głównie magazyn (eventy rozlicza przy evencie) — magazyn pierwszy
  if (u.role === 'EVENT') return u.warehouseWorkEnabled ? ['WAREHOUSE', 'NIGHT_WAREHOUSE', 'EVENT', 'EVENT_DAILY'] : ['EVENT', 'EVENT_DAILY'];
  const t = [];
  if (u.role === 'WAREHOUSE' || u.warehouseWorkEnabled) t.push('WAREHOUSE', 'NIGHT_WAREHOUSE'); // NIGHT_EVENT wycofany (T33)
  if (u.eventWorkEnabled) t.push('EVENT', 'EVENT_DAILY'); // T135: dniówka u każdego z pracą na eventach
  return t.length ? t : ['EVENT'];
};
// Formularz startuje z pierwszym typem dozwolonym dla roli. Wcześniej zawsze 'WAREHOUSE' —
// u eventowego pole pokazywało "Event", a wysyłało "Magazyn" po pustej stawce = 0 zł (T40).
const emptyForm = (u) => {
  const workType = workTypesFor(u).filter(w => !['EVENT', 'EVENT_DAILY'].includes(w))[0] || 'WAREHOUSE'; // T154: nowy wpis — magazyn
  return { date: new Date().toISOString().split('T')[0], workType, startTime: '07:00', endTime: '15:00', location: '', notes: '' };
};
const emptyBonusForm = () => ({ date: new Date().toISOString().split('T')[0], bonusType: 'CONTAINER', amount: '', description: '' });

// T151/T155: expensesOnly = ekran „Dodatki do rozliczenia” — same dodatki (Moje / Zespół); bez niego — same godziny
export default function HoursPage({ expensesOnly = false }) {
  const { user, isAdmin, canReviewHours, canApproveEventHours, canApproveHours, canAddHours, canAddWarehouseHours } = useAuth();
  // Kolumny "Pracownik"/"Rola" i filtr po pracowniku — dla każdego, kto widzi cudze wpisy,
  // niezależnie od roli (Biuro z uprawnieniem też) — T28; opiekun eventowych też (T77)
  const seesOthers = canReviewHours;
  const [hours, setHours] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // wpis w edycji (null = nowy)
  const [showBonus, setShowBonus] = useState(false);
  const [editingBonus, setEditingBonus] = useState(null); // dodatek w edycji (null = nowy)
  const [form, setForm] = useState(emptyForm(user));
  const [formStart, setFormStart] = useState(emptyForm(user));   // stan początkowy (T62)
  const [bonusStart, setBonusStart] = useState(emptyBonusForm());
  const [bonusForm, setBonusForm] = useState(emptyBonusForm());
  const [bonusFile, setBonusFile] = useState(null); // T60: paragon / faktura do dodatku
  // T68: „Moje" / „Zespół"; T123: kto sam nie wpisuje godzin (np. koordynator na etacie) — od razu i tylko „Zespół"
  // T155: dodatki zgłasza każdy (poza eventowym) — tam zawsze start od „Moje”
  const [scope, setScope] = useState((expensesOnly && !isAdmin) || canAddHours || !canReviewHours ? 'mine' : 'team');
  // Kolumny „Pracownik"/„Rola" mają sens tylko tam, gdzie widać cudze wpisy
  const [selH, setSelH] = useState({});     // T15a: zaznaczone wpisy godzin (id → true)
  const [selB, setSelB] = useState({});     // T15a: zaznaczone dodatki
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(''); // filtr po pracowniku (id)
  const [reviewer, setReviewer] = useState(''); // T79: filtr „rozpatrzył" ('' = wszyscy, 'none' = jeszcze nikt)
  const [norm, setNorm] = useState(null);       // T98: norma i nadgodziny magazynowego (miesiąc z filtra „Od")
  const [onlyPending, setOnlyPending] = useState(false);
  const df = useDateFilter();
  const { show, ToastComponent } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Skrót "+ Godziny" z Dashboardu (T48.4); eventy rozlicza się w „Eventy” (T154)
  useEffect(() => {
    if (searchParams.get('new') && user) { setSearchParams({}, { replace: true }); if (expensesOnly) openNewBonus(); else if (canAddWarehouseHours) openNew(); } // T151, T154, T155
    // Z paska „Czeka na Ciebie" (T89): od razu zakładka „Zespół" i tylko oczekujące
    if (searchParams.get('scope') === 'team' && user) { setSearchParams({}, { replace: true }); setScope('team'); setOnlyPending(true); }
  }, [searchParams, user]);


  // Filtr po pracowniku + sortowanie (po stronie przeglądarki — listy są małe)
  const people = uniqueUsers([...hours.map(h => h.user), ...bonuses.map(b => b.user)]);
  // T68: „Moje" = tylko własne wpisy (z kafelkami), „Zespół" = cudze (do zatwierdzania)
  const inScope = (rows) => seesOthers
    ? rows.filter(r => scope === 'mine' ? r.userId === user?.id : r.userId !== user?.id)
    : rows;
  // Filtry zakładki „Zespół": pracownik, kto rozpatrzył (T79), tylko oczekujące
  const teamFilter = (rows) => rows
    .filter(r => !employee || r.userId === employee)
    .filter(r => scope !== 'team' || !onlyPending || r.status === 'PENDING')
    .filter(r => scope !== 'team' || !reviewer || (reviewer === 'none' ? r.status === 'PENDING' : r.approvedBy?.id === reviewer));
  const hoursVisible = inScope(teamFilter(hours));
  const bonusesVisible = inScope(teamFilter(bonuses));
  const reviewers = uniqueUsers([...hours, ...bonuses].map(r => r.approvedBy).filter(Boolean));
  const showWho = seesOthers && scope === 'team';
  const shown = expensesOnly ? bonuses : hours; // T155: liczniki zakładek — tylko to, co jest na tym ekranie
  const myCount = shown.filter(x => x.userId === user?.id).length;
  const teamCount = shown.filter(x => x.userId !== user?.id).length;
  const hs = useSort(hoursVisible, HOURS_SORT, 'date');
  const bs = useSort(bonusesVisible, BONUS_SORT, 'date');

  const load = useCallback(async () => {
    try {
      // „tylko oczekujące" (zakładka Zespół): wszystkie oczekujące, bez względu na daty — jak licznik w pasku (T89)
      const params = onlyPending && scope === 'team' ? 'status=PENDING' : `from=${df.from}&to=${df.to}`;
      const [h, b] = await Promise.all([api.getHours(params), api.getBonuses(params)]);
      setHours(h);
      setBonuses(b);
      if (user?.role === 'WAREHOUSE') api.getMonthSummary(String(df.from).slice(0, 7)).then(setNorm).catch(() => setNorm(null));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [df.from, df.to, onlyPending, scope]);

  useEffect(() => { load(); }, [load]);
  // T66: moje odbyte eventy bez wpisu godzin — do wyboru w formularzu

  // Wspólna obsługa akcji: komunikat dopiero po sukcesie, błąd z API w toaście
  const act = async (fn, okMessage) => {
    try {
      await fn();
      show(okMessage);
      load();
    } catch (err) { show(err.message, 'error'); }
  };

  const openNew = () => { const f = emptyForm(user); setEditing(null); setForm(f); setFormStart(f); setShowForm(true); };
  const openEdit = (h) => {
    setEditing(h);
    // Wpis z typem spoza listy roli (np. stary "Magazyn" u eventowego) — podstaw pierwszy dozwolony
    const allowed = workTypesFor(h.user);
    const workType = allowed.includes(h.workType) ? h.workType : allowed[0];
    setForm({
      date: String(h.date).slice(0, 10), workType,
      startTime: h.startTime || '07:00', endTime: h.endTime || '15:00',
      location: h.location || '', notes: h.notes || '',
      eventId: h.eventId || null, eventName: h.event?.name || null,
    });
    setFormStart({
      date: String(h.date).slice(0, 10), workType,
      startTime: h.startTime || '07:00', endTime: h.endTime || '15:00',
      location: h.location || '', notes: h.notes || '',
      eventId: h.eventId || null, eventName: h.event?.name || null,
    });
    setShowForm(true);
  };
  const setWorkType = (workType) => setForm(f => ({ ...f, workType }));


  const handleSubmitHours = async () => {
    try {
      if (editing) {
        await api.updateHours(editing.id, form);
        show('Wpis zapisany!');
      } else {
        await api.createHours(form);
        // T85: wpis z opóźnieniem — mówimy wprost, kiedy dodano (zatwierdzający też to widzi)
        const today = new Date().toISOString().slice(0, 10);
        show(form.date && form.date < today ? `Zapisano godziny z ${fmtDay(form.date)} — dodane dziś (${fmtDay(new Date())}).` : 'Godziny dodane!');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) { show(err.message, 'error'); }
  };

  const handleDeleteHours = (h) => {
    if (!window.confirm(`Usunąć wpis z ${new Date(h.date).toLocaleDateString('pl-PL')} (${Number(h.totalHours)}h)?`)) return;
    act(() => api.deleteHours(h.id), 'Wpis usunięty.');
  };

  const openNewBonus = () => { const b = { ...emptyBonusForm(), ...(canAddHours ? {} : { bonusType: 'EXPENSE' }) }; setEditingBonus(null); setBonusForm(b); setBonusStart(b); setBonusFile(null); setShowBonus(true); };
  const openEditBonus = (b) => {
    setEditingBonus(b);
    const bf = { date: String(b.date).slice(0, 10), bonusType: b.bonusType, amount: Number(b.amount), description: b.description || '' };
    setBonusForm(bf); setBonusStart(bf);
    setBonusFile(null);
    setShowBonus(true);
  };
  const closeBonus = () => { setShowBonus(false); setEditingBonus(null); };

  const bonusNeedsDesc = BONUS_NEEDS_DESC.includes(bonusForm.bonusType);
  const bonusDescOk = !bonusNeedsDesc || bonusForm.description.trim().length >= BONUS_MIN_DESC;

  const handleSubmitBonus = async () => {
    if (!bonusDescOk) { show(`Opis jest wymagany — co najmniej ${BONUS_MIN_DESC} znaków.`, 'error'); return; }
    try {
      let id = editingBonus?.id;
      if (editingBonus) {
        await api.updateBonus(id, bonusForm);
      } else {
        id = (await api.createBonus(bonusForm)).id;
      }
      // Załącznik dogrywa się po zapisie dodatku (T60); błąd pliku nie cofa dodatku
      if (bonusFile && id) {
        try { await api.uploadAttachment('BONUS', id, bonusFile); }
        catch (e) { show(`Dodatek zapisany, ale załącznik nie: ${e.message}`, 'error'); closeBonus(); load(); return; }
      }
      show(editingBonus ? 'Dodatek zapisany!' : 'Dodatek zgłoszony!');
      closeBonus();
      load();
    } catch (err) { show(err.message, 'error'); }
  };

  const handleDeleteBonus = (b) => {
    if (!window.confirm(`Usunąć dodatek ${bonusLabel(b.bonusType)} (${Number(b.amount).toFixed(0)} zł)?`)) return;
    act(() => api.deleteBonus(b.id), 'Dodatek usunięty.');
  };

  // Czy zalogowany może zmienić/usunąć dany wpis lub dodatek (lustro reguł z backendu)
  const canModify = (item) => {
    if (item.settlementId || item.settlement) return false; // T87: wypłacone — zablokowane
    if (item.userId === user?.id) return item.status === 'PENDING' || item.status === 'REJECTED'; // T148: odrzucony — autor poprawia
    if (item.status === 'APPROVED' && !isAdmin) return false; // T95: zatwierdzone — zablokowane
    return canReviewHours;
  };

  // T15a: co da się zrobić zbiorczo z zaznaczonymi — zatwierdzić (cudze oczekujące, mam prawo)
  // albo usunąć (własne oczekujące, lub cudze gdy mam prawo)
  const canApproveItem = (it) => canReviewHours && it.userId !== user?.id && it.status === 'PENDING' && !it.settlementId;
  // T95: zatwierdzenie cofa zatwierdzający albo admin (wpis wraca do „Oczekuje")
  const canUndo = (it) => it.status === 'APPROVED' && !it.settlementId && !it.settlement && (isAdmin || it.approvedBy?.id === user?.id);
  const undo = (it, bonus) => {
    if (!window.confirm('Cofnąć zatwierdzenie? Wpis wróci do „Oczekuje” i będzie można go poprawić.')) return;
    act(() => bonus ? api.unapproveBonus(it.id) : api.unapproveHours(it.id), 'Zatwierdzenie cofnięte');
  };
  const bulk = (rows, sel) => {
    const chosen = rows.filter(r => sel[r.id]);
    return { chosen, toApprove: chosen.filter(canApproveItem), toDelete: chosen.filter(canModify) };
  };
  const bulkH = bulk(hs.sorted, selH);
  const bulkB = bulk(bs.sorted, selB);
  const toggleAll = (rows, sel, setSel) => {
    const selectable = rows.filter(r => canApproveItem(r) || canModify(r));
    const all = selectable.length > 0 && selectable.every(r => sel[r.id]);
    setSel(all ? {} : Object.fromEntries(selectable.map(r => [r.id, true])));
  };
  const bulkAct = async (fn, setSel) => { await act(fn, 'Gotowe'); setSel({}); };

  // Podsumowanie WŁASNYCH wpisów (koordynator widzi też zespół — tego nie sumujemy): T39
  const myStats = summarizeHours(hours.filter(h => h.userId === user?.id));

  // Dodatki własne: odrzucone nie liczą się do sumy
  const bonusStats = bonuses.reduce((acc, b) => {
    if (b.userId !== user?.id || b.status === 'REJECTED') return acc;
    acc.amount += Number(b.amount) || 0;
    if (b.status === 'PENDING') acc.pending += 1;
    return acc;
  }, { amount: 0, pending: 0 });

  const formUser = editing ? editing.user : user;

  // T79/T85: eksport wpisów z widoku — z datą dodania i osobą, która rozpatrzyła
  const exportCsv = () => {
    const d = (x) => x ? new Date(x).toLocaleDateString('pl-PL') : '';
    const who = (r) => r.approvedBy ? `${r.approvedBy.firstName} ${r.approvedBy.lastName}` : '';
    const st = { PENDING: 'oczekuje', APPROVED: 'zatwierdzone', REJECTED: 'odrzucone' };
    const rows = [['Pracownik', 'Data pracy', 'Dodano', 'Rodzaj', 'Event', 'Od', 'Do', 'Godziny', 'Kwota zł', 'Status', 'Rozpatrzył', 'Data rozpatrzenia', 'Wypłacone']];
    hs.sorted.forEach(h => rows.push([fullName(h.user), d(h.date), d(h.createdAt), workLabel(h.workType), h.event?.name || '', h.startTime || '', h.endTime || '',
      h.workType === 'EVENT_DAILY' ? 'dniówka' : String(Number(h.totalHours)).replace('.', ','), Number(h.totalAmount).toFixed(2).replace('.', ','),
      st[h.status] || h.status, who(h), h.status !== 'PENDING' ? d(h.approvedAt) : '', h.settlementId ? 'tak' : '']));
    bs.sorted.forEach(b => rows.push([fullName(b.user), d(b.date), d(b.createdAt), 'Dodatek: ' + bonusLabel(b.bonusType), b.event?.name || '', '', '', '',
      Number(b.amount).toFixed(2).replace('.', ','), st[b.status] || b.status, who(b), b.status !== 'PENDING' ? d(b.approvedAt) : '', b.settlementId ? 'tak' : '']));
    const csv = '\uFEFF' + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `godziny_${df.from}_${df.to}.csv`; a.click();
  };

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">{expensesOnly ? 'Dodatki do rozliczenia' : 'Godziny pracy'}</h2>
        {expensesOnly && !isAdmin && <button className="btn btn-warning" onClick={openNewBonus}>+ Dodatek</button>}
        {!expensesOnly && canAddWarehouseHours && <button className="btn btn-primary" onClick={openNew}>+ Godziny</button>}
      </div>

      {expensesOnly && !isAdmin && (!seesOthers || scope === 'mine') && (
        <div style={{ fontSize: 12, color: '#475569', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
          🧾 {canAddHours ? 'Rozpakowanie kontenera, transport, zakupy, wyłożone ze swoich' : 'Zakupy dla firmy, wyłożone ze swoich, transport'} — dodaj z opisem (i zdjęciem paragonu). Zatwierdza przełożony, po zatwierdzeniu trafia do wypłaty.
        </div>
      )}
      {!expensesOnly && !canAddWarehouseHours && canAddHours && (!seesOthers || scope === 'mine') && (
        <div style={{ fontSize: 11, color: '#64748B' }}>Godziny z eventów rozliczasz w „Eventy” → „Rozlicz godziny” (po evencie). Dodatki — w „Dodatki do rozliczenia”.</div>
      )}
      {seesOthers && (canAddHours || (expensesOnly && !isAdmin)) && <ScopeTabs scope={scope} onChange={setScope} mineCount={myCount} teamCount={teamCount} />}
      {seesOthers && scope === 'team' && !!user?.canApproveEventHours && (
        <div style={{ fontSize: 11, color: '#475569', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: '6px 10px' }}>
          🎪 Jako opiekun eventowych widzisz {expensesOnly ? <><b>wydatki pod eventami i dodatki eventowych</b> wszystkich</> : <><b>wpisy eventowe wszystkich</b> (godziny pod eventem, dniówki)</>}{canApproveHours ? ' oraz wpisy swoich podwładnych' : ''}. {expensesOnly ? 'Pozostałe dodatki' : 'Godziny magazynowe'} zatwierdza przełożony pracownika.
        </div>
      )}

      <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo}
        onFilter={load} quickFilter={df.quick} onQuickFilter={df.handleQuick}>
        {seesOthers && scope === 'team' && <EmployeeFilter people={people} value={employee} onChange={setEmployee} />}
        {seesOthers && scope === 'team' && (
          <select value={reviewer} onChange={e => setReviewer(e.target.value)} title="Kto rozpatrzył wpis (T79)" style={{ fontSize: 12, maxWidth: 200 }}>
            <option value="">Rozpatrzył: wszyscy</option>
            <option value="none">jeszcze nikt (oczekuje)</option>
            {reviewers.map(r => <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>)}
          </select>
        )}
        {seesOthers && scope === 'team' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} /> tylko oczekujące <span style={{ color: '#94A3B8' }}>(wszystkie, bez względu na daty)</span>
          </label>
        )}
        {seesOthers && scope === 'team' && !expensesOnly && <button className="btn btn-outline btn-sm" onClick={exportCsv} title="Wpisy z widoku — z datą dodania i osobą, która rozpatrzyła">Pobierz CSV</button>}
      </DateFilter>

      {user?.role === 'WAREHOUSE' && !expensesOnly && (!seesOthers || scope === 'mine') && <NormCard s={norm} />}
      {expensesOnly && !isAdmin && (!seesOthers || scope === 'mine') && (
        <div className="flex gap-8 flex-wrap">
          <StatCard label="Dodatki" value={`${bonusStats.amount.toFixed(0)} zł`} accent="#F59E0B" sub={bonusStats.pending ? `${bonusStats.pending} oczekuje` : 'w tym okresie'} />
        </div>
      )}
      {canAddHours && !expensesOnly && (!seesOthers || scope === 'mine') && (
        <div className="flex gap-8 flex-wrap">
          <StatCard label="Godziny" value={`${myStats.hours}h`} accent="#059669" sub={`${myStats.hoursAmt.toFixed(0)} zł`} />
          <StatCard label="Dniówki" value={myStats.daily} accent="#D97706" sub={`${myStats.dailyAmt.toFixed(0)} zł`} />
          <StatCard label="Razem" value={`${myStats.total.toFixed(0)} zł`} accent="#2563EB"
            sub={myStats.pending ? `${myStats.pending} wpisów oczekuje` : 'godziny + dniówki (dodatki osobno)'} />
        </div>
      )}

      {!expensesOnly && <div className="card">
        {bulkH.chosen.length > 0 && (
          <div className="flex gap-6" style={{ alignItems: 'center', marginBottom: 8, fontSize: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 10px' }}>
            <b>Zaznaczone: {bulkH.chosen.length}</b>
            {bulkH.toApprove.length > 0 && <button className="btn btn-success btn-sm" onClick={() => bulkAct(() => api.approveBulkHours(bulkH.toApprove.map(r => r.id)), setSelH)}>✓ Zatwierdź ({bulkH.toApprove.length})</button>}
            {bulkH.toDelete.length > 0 && <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm(`Usunąć ${bulkH.toDelete.length} wpisów godzin?`)) bulkAct(() => api.deleteBulkHours(bulkH.toDelete.map(r => r.id)), setSelH); }}>🗑 Usuń ({bulkH.toDelete.length})</button>}
            <button className="btn btn-ghost btn-sm" onClick={() => setSelH({})}>odznacz</button>
          </div>
        )}
        {loading ? <div style={{ padding: 20, color: '#94A3B8' }}>Ładowanie...</div> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <th style={{ width: 28 }}><input type="checkbox" title="Zaznacz wszystkie, które da się zatwierdzić lub usunąć" onChange={() => toggleAll(hs.sorted, selH, setSelH)} checked={hs.sorted.some(r => selH[r.id]) && hs.sorted.filter(r => canApproveItem(r) || canModify(r)).every(r => selH[r.id])} /></th>
                {showWho && <SortTh label="Pracownik" k="employee" sort={hs.sort} onSort={hs.toggle} />}
                {showWho && <SortTh label="Rola" k="role" sort={hs.sort} onSort={hs.toggle} />}
                <SortTh label="Data" k="date" sort={hs.sort} onSort={hs.toggle} />
                <SortTh label="Typ" k="workType" sort={hs.sort} onSort={hs.toggle} />
                <th>Start</th><th>Koniec</th>
                <SortTh label="Godziny" k="totalHours" sort={hs.sort} onSort={hs.toggle} />
                <SortTh label="Kwota" k="totalAmount" sort={hs.sort} onSort={hs.toggle} />
                <SortTh label="Status / rozpatrzył" k="status" sort={hs.sort} onSort={hs.toggle} />
                <th>Akcja</th>
              </tr></thead>
              <tbody>
                {hs.sorted.map(h => (
                  <tr key={h.id} style={selH[h.id] ? { background: '#EFF6FF' } : undefined}>
                    <td>{(canApproveItem(h) || canModify(h)) && <input type="checkbox" checked={!!selH[h.id]} onChange={() => setSelH(s => ({ ...s, [h.id]: !s[h.id] }))} />}</td>
                    {showWho && <td><PersonCell name={fullName(h.user)} /></td>}
                    {showWho && <td><RoleBadge role={h.user?.role} /></td>}
                    <td>{new Date(h.date).toLocaleDateString('pl-PL')}<div><AddedOn date={h.date} createdAt={h.createdAt} /></div></td>
                    <td>{workLabel(h.workType)}{h.event && <div style={{ fontSize: 10, color: '#2563EB' }}>🎪 {h.event.name}</div>}</td>
                    <td>{h.startTime || '—'}</td>
                    <td>{h.endTime || '—'}</td>
                    <td><b>{h.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(h.totalHours)}h`}</b>
                      {Number(h.animationHours) > 0 && <div style={{ fontSize: 10, color: '#BE185D' }} title="Godziny animacji (czas trwania eventu) — stawka animacji">🎈 w tym animacja {Number(h.animationHours)} h</div>}
                      {Number(h.overtimeHours) > 0 && h.workType !== 'NIGHT_WAREHOUSE' && <div style={{ fontSize: 10, color: '#059669' }} title="Część ponad normę miesiąca — płatna">+{Number(h.overtimeHours)} h nadg.</div>}
                      {h.user?.role === 'WAREHOUSE' && h.workType === 'NIGHT_WAREHOUSE' && <div style={{ fontSize: 10, color: '#7C3AED' }} title="Godziny nocne są zawsze płatne stawką nocną">nocne — płatne</div>}
                      <TeamDiffBadge tc={h.teamCompare} /></td>
                    <td>{h.user?.role === 'WAREHOUSE' && ['WAREHOUSE', 'NIGHT_WAREHOUSE'].includes(h.workType) && !Number(h.totalAmount)
                      ? <span style={{ fontSize: 11, color: '#94A3B8' }} title="Godziny w normie miesiąca — w pensji">w normie</span>
                      : <>{Number(h.totalAmount).toFixed(0)} zł</>}</td>
                    <td><Badge status={h.status} />{h.status !== 'PENDING' && <div><ReviewedBy item={h} /></div>}</td>
                    <td>
                      <div className="flex gap-4">
                        {canApproveItem(h) && (
                          <>
                            <button className="btn btn-success btn-sm" title="Zatwierdź" onClick={() => act(() => api.approveHours(h.id), 'Zatwierdzone')}>✓</button>
                            <button className="btn btn-danger btn-sm" title="Odrzuć" onClick={() => { const r = askRejectReason(); if (r !== null) act(() => api.rejectHours(h.id, r), 'Odrzucone'); }}>✗</button>
                          </>
                        )}
                        {canUndo(h) && <button className="btn btn-outline btn-sm" title="Cofnij zatwierdzenie — wpis wróci do „Oczekuje”" onClick={() => undo(h, false)}>↩ Cofnij</button>}
                        {canModify(h) && (
                          <>
                            <button className={`btn ${h.status === 'REJECTED' ? 'btn-danger' : 'btn-outline'} btn-sm`} title={h.status === 'REJECTED' ? 'Popraw — wpis wróci do zatwierdzenia' : 'Edytuj'} onClick={() => openEdit(h)}>{h.status === 'REJECTED' ? '✎ Popraw' : '✎'}</button>
                            <button className="btn btn-outline btn-sm" title="Usuń" onClick={() => handleDeleteHours(h)}>🗑</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {hs.sorted.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak wpisów</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {expensesOnly && <div className="card">
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Dodatki</h3>
        {bulkB.chosen.length > 0 && (
          <div className="flex gap-6" style={{ alignItems: 'center', marginBottom: 8, fontSize: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 10px' }}>
            <b>Zaznaczone: {bulkB.chosen.length}</b>
            {bulkB.toApprove.length > 0 && <button className="btn btn-success btn-sm" onClick={() => bulkAct(() => api.approveBulkBonuses(bulkB.toApprove.map(r => r.id)), setSelB)}>✓ Zatwierdź ({bulkB.toApprove.length})</button>}
            {bulkB.toDelete.length > 0 && <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm(`Usunąć ${bulkB.toDelete.length} dodatków?`)) bulkAct(() => api.deleteBulkBonuses(bulkB.toDelete.map(r => r.id)), setSelB); }}>🗑 Usuń ({bulkB.toDelete.length})</button>}
            <button className="btn btn-ghost btn-sm" onClick={() => setSelB({})}>odznacz</button>
          </div>
        )}
        {loading ? <div style={{ padding: 20, color: '#94A3B8' }}>Ładowanie...</div> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <th style={{ width: 28 }}><input type="checkbox" title="Zaznacz wszystkie, które da się zatwierdzić lub usunąć" onChange={() => toggleAll(bs.sorted, selB, setSelB)} checked={bs.sorted.some(r => selB[r.id]) && bs.sorted.filter(r => canApproveItem(r) || canModify(r)).every(r => selB[r.id])} /></th>
                {showWho && <SortTh label="Pracownik" k="employee" sort={bs.sort} onSort={bs.toggle} />}
                {showWho && <SortTh label="Rola" k="role" sort={bs.sort} onSort={bs.toggle} />}
                <SortTh label="Data" k="date" sort={bs.sort} onSort={bs.toggle} />
                <SortTh label="Rodzaj" k="bonusType" sort={bs.sort} onSort={bs.toggle} />
                <SortTh label="Kwota" k="amount" sort={bs.sort} onSort={bs.toggle} />
                <th>Opis</th>
                <SortTh label="Status / rozpatrzył" k="status" sort={bs.sort} onSort={bs.toggle} />
                <th>Akcja</th>
              </tr></thead>
              <tbody>
                {bs.sorted.map(b => (
                  <tr key={b.id} style={selB[b.id] ? { background: '#EFF6FF' } : undefined}>
                    <td>{(canApproveItem(b) || canModify(b)) && <input type="checkbox" checked={!!selB[b.id]} onChange={() => setSelB(s => ({ ...s, [b.id]: !s[b.id] }))} />}</td>
                    {showWho && <td><PersonCell name={fullName(b.user)} /></td>}
                    {showWho && <td><RoleBadge role={b.user?.role} /></td>}
                    <td>{new Date(b.date).toLocaleDateString('pl-PL')}<div><AddedOn date={b.date} createdAt={b.createdAt} /></div></td>
                    <td>{bonusLabel(b.bonusType)}</td>
                    <td><b>{Number(b.amount).toFixed(0)} zł</b></td>
                    <td style={{ color: '#64748B' }}>
                      {b.description || '—'}
                      {b.attachments?.length > 0 && (
                        <div style={{ marginTop: 2 }}>
                          {b.attachments.map(a => (
                            <button key={a.id} className="btn btn-ghost btn-sm" style={{ padding: '0 4px', fontSize: 11 }} title={`${a.filename} (${Math.round(a.size / 1024)} KB)`}
                              onClick={() => api.openAttachment(a.id).catch(e => show(e.message, 'error'))}>📎 {a.mime === 'application/pdf' ? 'PDF' : 'zdjęcie'}</button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td><Badge status={b.status} />{b.status !== 'PENDING' && <div><ReviewedBy item={b} /></div>}{b.event && <div style={{ fontSize: 10, color: '#2563EB' }}>🎪 {b.event.name}</div>}</td>
                    <td>
                      <div className="flex gap-4">
                        {canApproveItem(b) && (
                          <>
                            <button className="btn btn-success btn-sm" title="Zatwierdź" onClick={() => act(() => api.approveBonus(b.id), 'Dodatek zatwierdzony')}>✓</button>
                            <button className="btn btn-danger btn-sm" title="Odrzuć" onClick={() => { const r = askRejectReason(); if (r !== null) act(() => api.rejectBonus(b.id, r), 'Dodatek odrzucony'); }}>✗</button>
                          </>
                        )}
                        {canUndo(b) && <button className="btn btn-outline btn-sm" title="Cofnij zatwierdzenie — dodatek wróci do „Oczekuje”" onClick={() => undo(b, true)}>↩ Cofnij</button>}
                        {canModify(b) && (
                          <>
                            <button className={`btn ${b.status === 'REJECTED' ? 'btn-danger' : 'btn-outline'} btn-sm`} title={b.status === 'REJECTED' ? 'Popraw — dodatek wróci do zatwierdzenia' : 'Edytuj'} onClick={() => openEditBonus(b)}>{b.status === 'REJECTED' ? '✎ Popraw' : '✎'}</button>
                            <button className="btn btn-outline btn-sm" title="Usuń" onClick={() => handleDeleteBonus(b)}>🗑</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {bs.sorted.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak dodatków</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {showForm && (
        <Modal title={editing ? 'Edytuj wpis godzin' : 'Dodaj godziny pracy'} onClose={() => { setShowForm(false); setEditing(null); }} dirty={isDirty(form, formStart)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {editing && editing.userId !== user?.id && (
              <div style={{ fontSize: 12, color: '#64748B' }}>Wpis pracownika: <b>{fullName(editing.user)}</b>. Zmiana zostanie zapisana w dzienniku zdarzeń.</div>
            )}
            {form.eventId ? (
              <div style={{ fontSize: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 10px', color: '#1E40AF' }}>
                🎪 Wpis pod eventem: <b>{form.eventName || editing?.event?.name}</b>.
              </div>
            ) : null}
            <div className="input-group"><label>Data</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="input-group"><label>Typ pracy</label>
              <select value={form.workType} onChange={e => setWorkType(e.target.value)}>
                {workTypesFor(formUser)
                  // T154: nowy wpis tu — tylko magazyn / nocna (eventy rozlicza się po evencie w „Eventy”); edycja — typ bez zmian
                  .filter(w => editing || !['EVENT', 'EVENT_DAILY'].includes(w))
                  // T144: pod eventem rozliczenie wg składu — dniówka albo godzinowo (ustawia koordynator)
                  .filter(w => !form.eventId || !form.eventPay || (form.eventPay === 'daily' ? w !== 'EVENT' : w !== 'EVENT_DAILY'))
                  .map(w => <option key={w} value={w}>{WORK_LABELS[w]}</option>)}
              </select>
              {form.eventId && form.eventPay && <div style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>Na tym evencie: <b>{form.eventPay === 'daily' ? 'dniówka' : 'godzinowo'}</b> — ustawił koordynator.</div>}
            </div>
            {form.workType === 'EVENT_DAILY' && (
              <div style={{ fontSize: 12, color: '#64748B' }}>Dniówka to stała kwota za cały dzień — bez godzin i bez przerwy. Jedna na dzień.</div>
            )}
            {form.workType !== 'EVENT_DAILY' && (
              <div className="flex gap-8">
                <div className="input-group flex-1"><label>Start</label><TimeSelect value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} /></div>
                <div className="input-group flex-1"><label>Koniec</label><TimeSelect value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} /></div>
              </div>
            )}
            {form.workType !== 'EVENT_DAILY' && (
              <div style={{ fontSize: 12, color: '#64748B' }}>Liczy się cały czas od startu do końca, bez odejmowania przerwy (np. 07:00–15:00 = 8 h).</div>
            )}
            <div className="input-group"><label>Lokalizacja / event</label><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="np. Magazyn, Wesele Poznań" /></div>
            <div className="input-group"><label>Uwagi</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Opcjonalnie..." /></div>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => { setShowForm(false); setEditing(null); }}>Anuluj</button>
              <button className="btn btn-primary" onClick={handleSubmitHours}>{editing ? 'Zapisz zmiany' : 'Zapisz'}</button>
            </div>
          </div>
        </Modal>
      )}

      {showBonus && (
        <Modal title={editingBonus ? 'Edytuj dodatek' : 'Zgłoś dodatek'} onClose={closeBonus} dirty={isDirty(bonusForm, bonusStart) || !!bonusFile}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {editingBonus && editingBonus.userId !== user?.id && (
              <div style={{ fontSize: 12, color: '#64748B' }}>Dodatek pracownika: <b>{fullName(editingBonus.user)}</b>. Zmiana zostanie zapisana w dzienniku zdarzeń.</div>
            )}
            <div className="input-group"><label>Data</label><input type="date" value={bonusForm.date} onChange={e => setBonusForm({ ...bonusForm, date: e.target.value })} /></div>
            <div className="input-group"><label>Rodzaj</label>
              <select value={bonusForm.bonusType} onChange={e => { setBonusForm({ ...bonusForm, bonusType: e.target.value }); if (!BONUS_NEEDS_DESC.includes(e.target.value)) setBonusFile(null); }}>
                {BONUS_PICKABLE.filter(k => canAddHours || (editingBonus && editingBonus.userId !== user?.id) || EXPENSE_ONLY_TYPES.includes(k)).filter(k => !(k === 'DISCRETIONARY' && (!editingBonus || editingBonus.userId === user?.id) && user?.role === 'EVENT')).map(k => <option key={k} value={k}>{BONUS_LABELS[k]}</option>)}
                {/* stary typ przy edycji starego wpisu — żeby select nie pokazywał czegoś innego niż wysyła */}
                {!BONUS_PICKABLE.includes(bonusForm.bonusType) && <option value={bonusForm.bonusType}>{bonusLabel(bonusForm.bonusType)}</option>}
              </select>
            </div>
            <div className="input-group"><label>Kwota (zł) <span style={{ fontWeight: 400, color: '#94A3B8' }}>— max {BONUS_MAX_AMOUNT} zł</span></label><input type="number" min="1" max={BONUS_MAX_AMOUNT} value={bonusForm.amount} onChange={e => setBonusForm({ ...bonusForm, amount: e.target.value })} placeholder="200" />
              {Number(bonusForm.amount) > BONUS_MAX_AMOUNT && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>Za dużo — jeden dodatek to maksymalnie {BONUS_MAX_AMOUNT} zł. Sprawdź, czy nie ma literówki.</div>}
            </div>
            <div className="input-group">
              <label>Opis{bonusNeedsDesc && <span style={{ color: '#DC2626' }}> * (wymagany, min. {BONUS_MIN_DESC} znaków)</span>}</label>
              <textarea value={bonusForm.description} onChange={e => setBonusForm({ ...bonusForm, description: e.target.value })}
                placeholder={bonusForm.bonusType === 'EXPENSE' ? 'Co zostało kupione i na jaki event...' : 'Co zostało zrobione...'} />
              {bonusNeedsDesc && !bonusDescOk && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>Jeszcze {BONUS_MIN_DESC - bonusForm.description.trim().length} znaków.</div>}
            </div>
            {BONUS_NEEDS_DESC.includes(bonusForm.bonusType) && (
            <div className="input-group">
              <label>Załącznik (paragon, faktura) — zdjęcie lub PDF, do 5 MB</label>
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment"
                onChange={e => { const f = e.target.files?.[0] || null; const err = api.checkFile(f); if (err) { show(err, 'error'); e.target.value = ''; setBonusFile(null); } else setBonusFile(f); }} />
              {bonusFile && <div style={{ fontSize: 10, color: '#059669', marginTop: 2 }}>✓ {bonusFile.name} ({(bonusFile.size / 1024).toFixed(0)} KB)</div>}
              {editingBonus?.attachments?.length > 0 && <div style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>Już dołączone: {editingBonus.attachments.map(a => a.filename).join(', ')}</div>}
            </div>
            )}
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={closeBonus}>Anuluj</button>
              <button className="btn btn-primary" onClick={handleSubmitBonus} disabled={!bonusDescOk || Number(bonusForm.amount) > BONUS_MAX_AMOUNT}>{editingBonus ? 'Zapisz zmiany' : 'Wyślij'}</button>
            </div>
          </div>
        </Modal>
      )}
      {ToastComponent}
    </div>
  );
}
